import { useState, useEffect } from 'react';
import { timeEntryController } from '../../controllers/timeEntryController';
import { projectController } from '../../controllers/projectController';
import { taskController } from '../../controllers/taskcontroller';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, 
  ChevronUp, ChevronDown, Play, Square, Clock, DollarSign, 
  Calendar, TrendingUp, CheckCircle
} from 'lucide-react';

const TimeTrackingPage = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterBillable, setFilterBillable] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [runningTimer, setRunningTimer] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [formData, setFormData] = useState({
    projectId: '',
    taskId: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    duration: 0,
    billable: true,
    hourlyRate: 0,
    status: 'Draft',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const itemsPerPage = 10;

  useEffect(() => { 
    fetchTimeEntries(); 
    fetchProjects();
    fetchTasks();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let result = [...timeEntries];
    
    if (searchQuery) {
      result = result.filter(e =>
        (e.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (e.timeEntryId || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterProject !== 'All') {
      result = result.filter(e => {
        const projectId = e.projectId?._id || e.projectId;
        return projectId === filterProject;
      });
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(e => e.status === filterStatus);
    }

    if (filterBillable !== 'All') {
      result = result.filter(e => e.billable === (filterBillable === 'Yes'));
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredEntries(result);
    setCurrentPage(1);
  }, [timeEntries, searchQuery, filterProject, filterStatus, filterBillable, sortField, sortDirection]);

  const fetchTimeEntries = async () => {
    setLoading(true);
    const result = await timeEntryController.getAll();
    if (result.success) {
      setTimeEntries(result.data);
      // Check for running timer
      const running = result.data.find(e => e.startTime && !e.endTime && e.date === new Date().toISOString().split('T')[0]);
      setRunningTimer(running || null);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    const result = await projectController.getAll();
    if (result.success) setProjects(result.data);
  };

  const fetchTasks = async () => {
    const result = await taskController.getAll();
    if (result.success) setTasks(result.data);
  };

  const getProjectName = (projectId) => {
    if (typeof projectId === 'object' && projectId !== null) {
      return projectId.name || projectId.projectName || 'Unknown';
    }
    const project = projects.find(p => (p.id || p._id) === projectId);
    return project ? (project.name || project.projectName) : 'Unknown';
  };

  const getTaskName = (taskId) => {
    if (!taskId) return 'N/A';
    if (typeof taskId === 'object' && taskId !== null) {
      return taskId.title || taskId.taskName || 'Unknown';
    }
    const task = tasks.find(t => (t.id || t._id) === taskId);
    return task ? (task.title || task.taskName) : 'Unknown';
  };

  const calculateTimerDuration = () => {
    if (!runningTimer || !runningTimer.startTime) return '0h 0m';
    const start = new Date(`${runningTimer.date}T${runningTimer.startTime}`);
    const diff = (currentTime - start) / 1000 / 60; // minutes
    const hours = Math.floor(diff / 60);
    const mins = Math.floor(diff % 60);
    return `${hours}h ${mins}m`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      projectId: '',
      taskId: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      startTime: '',
      endTime: '',
      duration: 0,
      billable: true,
      hourlyRate: 0,
      status: 'Draft',
      notes: ''
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    if (!dataToSend.taskId) delete dataToSend.taskId;
    
    const result = await timeEntryController.create(dataToSend);
    if (result.success) {
      setShowAddModal(false);
      fetchTimeEntries();
      alert('✅ Time entry created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    
    let projectIdValue = '';
    if (entry.projectId) {
      projectIdValue = typeof entry.projectId === 'object' 
        ? (entry.projectId.id || entry.projectId._id) 
        : entry.projectId;
    }

    let taskIdValue = '';
    if (entry.taskId) {
      taskIdValue = typeof entry.taskId === 'object' 
        ? (entry.taskId.id || entry.taskId._id) 
        : entry.taskId;
    }
    
    setFormData({
      projectId: projectIdValue,
      taskId: taskIdValue,
      description: entry.description || '',
      date: entry.date || '',
      startTime: entry.startTime || '',
      endTime: entry.endTime || '',
      duration: entry.duration || 0,
      billable: entry.billable !== undefined ? entry.billable : true,
      hourlyRate: entry.hourlyRate || 0,
      status: entry.status || 'Draft',
      notes: entry.notes || ''
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    if (!dataToSend.taskId) delete dataToSend.taskId;
    
    const result = await timeEntryController.update(selectedEntry.id || selectedEntry._id, dataToSend);
    if (result.success) {
      setShowEditModal(false);
      fetchTimeEntries();
      alert('✅ Time entry updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (entry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete time entry "${entry.description || entry.timeEntryId}"?`)) return;
    
    const result = await timeEntryController.delete(entry.id || entry._id);
    if (result?.success) {
      fetchTimeEntries();
      alert('✅ Time entry deleted!');
    } else {
      alert('Error: ' + result?.message);
    }
  };

  const handleStartTimer = async () => {
    if (runningTimer) {
      alert('Timer already running!');
      return;
    }

    const result = await timeEntryController.startTimer({
      projectId: formData.projectId || projects[0]?.id || projects[0]?._id,
      description: 'Working...',
      billable: true
    });

    if (result.success) {
      setRunningTimer(result.data);
      fetchTimeEntries();
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleStopTimer = async () => {
    if (!runningTimer) return;

    const result = await timeEntryController.stopTimer(runningTimer.id || runningTimer._id);
    if (result.success) {
      setRunningTimer(null);
      fetchTimeEntries();
      alert('✅ Timer stopped!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleStatusChange = async (entry, newStatus) => {
    const result = await timeEntryController.updateStatus(entry.id || entry._id, newStatus);
    if (result.success) {
      fetchTimeEntries();
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterProject !== 'All') filters.projectId = filterProject;
    if (filterStatus !== 'All') filters.status = filterStatus;
    
    const result = await timeEntryController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-entries-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSelectEntry = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEntries.length === currentEntries.length && currentEntries.length > 0) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(currentEntries.map(e => e.id || e._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEntries.length === 0) {
      alert('Please select entries to delete');
      return;
    }
    
    if (!window.confirm(`Delete ${selectedEntries.length} selected entries?`)) return;
    
    const result = await timeEntryController.bulkDelete(selectedEntries);
    if (result.success) {
      setSelectedEntries([]);
      fetchTimeEntries();
      alert('✅ Entries deleted!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedEntries.length === 0) {
      alert('Please select entries to update');
      return;
    }
    
    const result = await timeEntryController.bulkUpdate(selectedEntries, { status: newStatus });
    if (result.success) {
      setSelectedEntries([]);
      fetchTimeEntries();
      alert('✅ Entries updated!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEntries = filteredEntries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);

  const stats = {
    totalHours: timeEntries.reduce((sum, e) => sum + (e.calculateDuration() / 60), 0),
    totalEntries: timeEntries.length,
    billableHours: timeEntries.filter(e => e.billable).reduce((sum, e) => sum + (e.calculateDuration() / 60), 0),
    revenue: timeEntries.reduce((sum, e) => sum + e.calculateAmount(), 0),
    thisWeek: timeEntries.filter(e => {
      const date = new Date(e.date);
      const today = new Date();
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      return date >= weekAgo && date <= today;
    }).reduce((sum, e) => sum + (e.calculateDuration() / 60), 0)
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    dangerButton: { backgroundColor: '#ef4444', color: '#fff' },
    successButton: { backgroundColor: '#10b981', color: '#fff' },
    timerCard: { backgroundColor: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px', border: '2px solid #e2e8f0' },
    timerDisplay: { fontSize: '48px', fontWeight: 'bold', color: '#1e293b', textAlign: 'center', marginBottom: '16px', fontFamily: 'monospace' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', minHeight: '100px', resize: 'vertical' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
    checkbox: { width: '18px', height: '18px', cursor: 'pointer' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Time Tracking</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {selectedEntries.length > 0 && (
            <>
              <button style={{ ...styles.button, ...styles.dangerButton }} onClick={handleBulkDelete}>
                <Trash2 size={18} /> Delete ({selectedEntries.length})
              </button>
              <select style={styles.select} onChange={(e) => { if (e.target.value) { handleBulkStatusUpdate(e.target.value); e.target.value = ''; }}}>
                <option value="">Update Status</option>
                <option value="Draft">Draft</option>
                <option value="Submitted">Submitted</option>
                <option value="Approved">Approved</option>
              </select>
            </>
          )}
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Entry
          </button>
        </div>
      </div>

      {/* Timer Card */}
      <div style={styles.timerCard}>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Clock size={32} color="#3b82f6" style={{ margin: '0 auto' }} />
        </div>
        <div style={styles.timerDisplay}>
          {runningTimer ? calculateTimerDuration() : '0h 0m'}
        </div>
        <div style={{ textAlign: 'center' }}>
          {runningTimer ? (
            <div>
              <p style={{ marginBottom: '12px', color: '#64748b' }}>
                {runningTimer.description || 'Working...'}
              </p>
              <button style={{ ...styles.button, ...styles.dangerButton }} onClick={handleStopTimer}>
                <Square size={18} /> Stop Timer
              </button>
            </div>
          ) : (
            <button style={{ ...styles.button, ...styles.successButton }} onClick={handleStartTimer}>
              <Play size={18} /> Start Timer
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Hours</div>
            <Clock size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.totalHours.toFixed(1)}h</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>This Week</div>
            <Calendar size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.thisWeek.toFixed(1)}h</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Billable</div>
            <TrendingUp size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.billableHours.toFixed(1)}h</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Revenue</div>
            <DollarSign size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>${stats.revenue.toFixed(0)}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input type="text" placeholder="Search..." style={styles.searchInput} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <select style={styles.select} value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
          <option value="All">All Projects</option>
          {projects.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name || p.projectName}</option>)}
        </select>
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select style={styles.select} value={filterBillable} onChange={(e) => setFilterBillable(e.target.value)}>
          <option value="All">All Types</option>
          <option value="Yes">Billable</option>
          <option value="No">Non-Billable</option>
        </select>
      </div>

      {/* Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
        ) : currentEntries.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No time entries found.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '50px' }}>
                    <input type="checkbox" style={styles.checkbox} checked={selectedEntries.length === currentEntries.length && currentEntries.length > 0} onChange={handleSelectAll} />
                  </th>
                  <th style={styles.th} onClick={() => handleSort('date')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Date {sortField === 'date' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Duration</th>
                  <th style={styles.th}>Billable</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentEntries.map((entry) => {
                  const getStatusColor = (status) => {
                    const colors = { 'Draft': '#6b7280', 'Submitted': '#3b82f6', 'Approved': '#10b981', 'Rejected': '#ef4444' };
                    return colors[status] || '#6b7280';
                  };

                  return (
                    <tr key={entry.id || entry._id}>
                      <td style={styles.td}>
                        <input type="checkbox" style={styles.checkbox} checked={selectedEntries.includes(entry.id || entry._id)} onChange={() => handleSelectEntry(entry.id || entry._id)} onClick={(e) => e.stopPropagation()} />
                      </td>
                      <td style={styles.td}>{new Date(entry.date).toLocaleDateString()}</td>
                      <td style={styles.td} onClick={() => handleView(entry)}>
                        <div style={{ fontWeight: '500' }}>{entry.description || 'No description'}</div>
                        {entry.taskId && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{getTaskName(entry.taskId)}</div>}
                      </td>
                      <td style={styles.td}>{getProjectName(entry.projectId)}</td>
                      <td style={styles.td}><span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{entry.getDurationFormatted()}</span></td>
                      <td style={styles.td}>
                        {entry.billable ? (
                          <span style={{ ...styles.badge, backgroundColor: '#dcfce720', color: '#16a34a' }}>✓ Billable</span>
                        ) : (
                          <span style={{ ...styles.badge, backgroundColor: '#f1f5f920', color: '#64748b' }}>Non-Billable</span>
                        )}
                      </td>
                      <td style={styles.td}><span style={{ fontWeight: '600', color: '#10b981' }}>${entry.calculateAmount().toFixed(2)}</span></td>
                      <td style={styles.td}>
                        <select style={{ ...styles.badge, backgroundColor: `${getStatusColor(entry.status)}20`, color: getStatusColor(entry.status), border: 'none', cursor: 'pointer' }} value={entry.status} onChange={(e) => { e.stopPropagation(); handleStatusChange(entry, e.target.value); }} onClick={(e) => e.stopPropagation()}>
                          <option value="Draft">Draft</option>
                          <option value="Submitted">Submitted</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleView(entry); }} title="View"><Eye size={18} color="#64748b" /></button>
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleEdit(entry); }} title="Edit"><Edit2 size={18} color="#3b82f6" /></button>
                          <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleDelete(entry); }} title="Delete"><Trash2 size={18} color="#ef4444" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredEntries.length)} of {filteredEntries.length}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>Page {currentPage} of {totalPages}</div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add Time Entry' : 'Edit Time Entry'}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}><X size={20} /></button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}><AlertCircle size={14} />{error}</div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Project *</label>
                    <select name="projectId" style={styles.input} value={formData.projectId} onChange={handleInputChange} required>
                      <option value="">Select Project</option>
                      {projects.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name || p.projectName}</option>)}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Task (Optional)</label>
                    <select name="taskId" style={styles.input} value={formData.taskId} onChange={handleInputChange}>
                      <option value="">No Task</option>
                      {tasks.map(t => <option key={t.id || t._id} value={t.id || t._id}>{t.title || t.taskName}</option>)}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Date *</label>
                    <input type="date" name="date" style={styles.input} value={formData.date} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                      <option value="Draft">Draft</option>
                      <option value="Submitted">Submitted</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Start Time</label>
                    <input type="time" name="startTime" style={styles.input} value={formData.startTime} onChange={handleInputChange} />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>End Time</label>
                    <input type="time" name="endTime" style={styles.input} value={formData.endTime} onChange={handleInputChange} />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Hourly Rate ($)</label>
                    <input type="number" name="hourlyRate" style={styles.input} value={formData.hourlyRate} onChange={handleInputChange} min="0" step="0.01" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      <input type="checkbox" name="billable" checked={formData.billable} onChange={handleInputChange} style={{ marginRight: '8px' }} />
                      Billable
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <input type="text" name="description" style={styles.input} value={formData.description} onChange={handleInputChange} placeholder="What did you work on?" />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Notes</label>
                  <textarea name="notes" style={styles.textarea} value={formData.notes} onChange={handleInputChange} placeholder="Additional notes..." />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>Cancel</button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>{formSubmitting ? 'Saving...' : (showAddModal ? 'Add Entry' : 'Update Entry')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedEntry && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Time Entry Details</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => setShowViewModal(false)}><X size={20} /></button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Entry ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEntry.timeEntryId}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ ...styles.badge, backgroundColor: `${selectedEntry.getStatusColor()}20`, color: selectedEntry.getStatusColor() }}>{selectedEntry.status}</span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{new Date(selectedEntry.date).toLocaleDateString()}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Duration</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', fontFamily: 'monospace' }}>{selectedEntry.getDurationFormatted()}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{getProjectName(selectedEntry.projectId)}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Task</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{getTaskName(selectedEntry.taskId)}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Time</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEntry.startTime || 'N/A'} - {selectedEntry.endTime || 'N/A'}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Amount</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#10b981' }}>${selectedEntry.calculateAmount().toFixed(2)}</div>
                </div>

                {selectedEntry.description && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Description</div>
                    <div style={{ fontSize: '14px', color: '#1e293b', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>{selectedEntry.description}</div>
                  </div>
                )}

                {selectedEntry.notes && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Notes</div>
                    <div style={{ fontSize: '14px', color: '#1e293b', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>{selectedEntry.notes}</div>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedEntry); }}><Edit2 size={16} /> Edit</button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTrackingPage;